import { processSocketMessage, IApplicationState, SET_SOCKET_STATUS } from '@spinwise/spinwise-core';
import axios from 'axios';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getClientCookie } from 'Utils/cookies';

const socket: { _instance: WebSocket } = {
  _instance: null,
};

const safelyParseJson = (data: unknown): Record<any, any> | null => {
  try {
    if (typeof data !== 'string') throw new Error();
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

const SocketManager = () => {
  const isLoggedIn = useSelector((state: IApplicationState) => state.authReducer?.auth?.success);
  const dispatch = useDispatch();
  const unparsableMessages = useRef<any[]>([]);

  const socketAddress = useRef<string>(null);
  const reconnectConfig = useRef(null);
  const inactivityTimeoutMs = useRef<number>(null);

  const reconnectAttempts = useRef(0);
  const reconnectTimeoutId = useRef<NodeJS.Timeout>(null);
  const inactivityTimeoutId = useRef<NodeJS.Timeout>(null);

  useEffect(() => {
    if (isLoggedIn && !socket._instance) {
      connectSocket();
    }

    return () => {
      if (reconnectTimeoutId.current) {
        clearTimeout(reconnectTimeoutId.current);
      }
      if (inactivityTimeoutId.current) {
        clearTimeout(inactivityTimeoutId.current);
      }

      if (socket._instance) {
        socket._instance.onclose = null;
        socket._instance.close();
        socket._instance = null;
      }
    };
  }, [isLoggedIn]);

  const resetInactivityTimer = () => {
    if (!inactivityTimeoutMs.current) return;

    if (inactivityTimeoutId.current) {
      clearTimeout(inactivityTimeoutId.current);
    }

    const timeout = inactivityTimeoutMs.current;

    inactivityTimeoutId.current = setTimeout(() => {
      if (socket._instance) {
        console.warn(`[WebSocket] No message received for ${timeout / 1000} seconds. Closing connection.`);
        socket._instance.close();
      }
    }, timeout);
  };

  const handleSocketMessage = event => {
    resetInactivityTimer();

    const eventData = safelyParseJson(event.data);
    if (!eventData && unparsableMessages.current.length >= 3) {
      socket._instance?.close?.();
    }
    if (!eventData) {
      console.error('WebSocket: Failed to parse incoming socket message.', event.data);
      unparsableMessages.current.push(event.data);
      return;
    }
    unparsableMessages.current = [];
    processSocketMessage(eventData)(dispatch);
  };

  const scheduleReconnect = () => {
    const { maxDelay, initialDelay, jitter } = reconnectConfig.current;

    reconnectAttempts.current = reconnectAttempts.current + 1;

    // Double the wait time for each subsequent reconnect attempt, up to a maximum limi
    const delay = Math.min(maxDelay, initialDelay * Math.pow(2, reconnectAttempts.current));

    // Random factor (jitter) to prevent multiple clients from retrying in perfect sync, which could overload the server
    const finalDelay = delay + Math.random() * jitter;

    reconnectTimeoutId.current = setTimeout(connectSocket, finalDelay);
  };

  const handleSocketOpenConnection = () => {
    dispatch({
      type: SET_SOCKET_STATUS,
      data: 'CONNECTED',
    });

    reconnectAttempts.current = 0;
    if (reconnectTimeoutId.current) {
      clearTimeout(reconnectTimeoutId.current);
    }

    resetInactivityTimer();
  };

  const handleSocketError = error => {
    console.error('[WebSocket] [onerror]', error);
    dispatch({
      type: SET_SOCKET_STATUS,
      data: 'DISCONNECTED',
    });
  };

  const handleSocketCloseConnection = event => {
    if (inactivityTimeoutId.current) {
      clearTimeout(inactivityTimeoutId.current);
    }

    socket._instance = null;
    dispatch({
      type: SET_SOCKET_STATUS,
      data: 'DISCONNECTED',
    });

    if (event.code === 1006) {
      scheduleReconnect();
      console.warn(`[WebSocket] [onclose]: Connection closed. Code: 1006. Will try to reconnect.`);
      return;
    }
    console.warn(`[WebSocket] [onclose]: Connection closed. Code: ${event.code}.`);
  };

  const connectSocket = async () => {
    try {
      if (inactivityTimeoutId.current) {
        clearTimeout(inactivityTimeoutId.current);
      }

      if (!socketAddress.current) {
        const response = await axios.get('/api/socket');
        const data = response.data ?? {};
        const { initialDelay, maxDelay, jitter } = data.reconnectConfig ?? {};
        if (!data.socketAddress || !initialDelay || !maxDelay || !jitter || !inactivityTimeoutMs) {
          throw new Error('Socket configuration missing or incomplete. Will not connect.');
        }

        socketAddress.current = data.socketAddress;
        reconnectConfig.current = data.reconnectConfig;
        inactivityTimeoutMs.current = data.inactivityTimeoutMs;
      }

      dispatch({
        type: SET_SOCKET_STATUS,
        data: 'CONNECTING',
      });

      const uuid = getClientCookie('uuid');

      socket._instance = new WebSocket(`${socketAddress.current}?uuid=${uuid}`);

      socket._instance.onopen = handleSocketOpenConnection;
      socket._instance.onmessage = handleSocketMessage;
      socket._instance.onerror = handleSocketError;
      socket._instance.onclose = handleSocketCloseConnection;
    } catch (error) {
      console.error('[WebSocket] [catch]', error);
      dispatch({
        type: SET_SOCKET_STATUS,
        data: 'DISCONNECTED',
      });
    }
  };

  return null;
};

export default SocketManager;
